import { FC, useLayoutEffect } from 'react';

const LABELLED_BY = 'aria-labelledby';
const DESCRIBED_BY = 'aria-describedby';

export interface IDialogAriaProps {
    /** id корня Mantine (`Modal.Root id`, `Drawer.Root id`) */
    rootId: string;
    /** id подписи своей разметки (шапка без `Title`, заголовок в содержимом); без него подпись окна не трогается */
    labelledBy?: string;
    /** id описания (через пробел — несколько); без него описания нет */
    describedBy?: string;
}

/**
 * Имя и описание окна Mantine (`ModalBase`: Modal, Drawer). Mantine подписывает окно только своим заголовком (`Title`)
 * и описывает его всем телом (`aria-describedby` — id `Body`): диктор при открытии читал бы всё содержимое. Окно
 * получает подпись `labelledBy` и описание `describedBy` или остаётся без описания. Mantine ставит свои значения
 * следующим коммитом после вставки тела — значения держит наблюдатель. Рендерится внутри тела окна.
 */
export const DialogAria: FC<IDialogAriaProps> = ({ rootId, labelledBy, describedBy }) => {
    useLayoutEffect(() => {
        const dialog = document.getElementById(rootId)?.querySelector('[role="dialog"]');
        if (!dialog) return;
        const set = (name: string, value: string | undefined) => {
            if (!value) {
                if (dialog.hasAttribute(name)) dialog.removeAttribute(name);
            } else if (dialog.getAttribute(name) !== value) {
                dialog.setAttribute(name, value);
            }
        };
        const apply = () => {
            if (labelledBy) set(LABELLED_BY, labelledBy);
            set(DESCRIBED_BY, describedBy);
        };
        apply();
        const observer = new MutationObserver(apply);
        observer.observe(dialog, { attributes: true, attributeFilter: [LABELLED_BY, DESCRIBED_BY] });
        return () => {
            observer.disconnect();
            // своя подпись уходит вместе с ней: окно без неё не подписано чужим id
            if (labelledBy && dialog.getAttribute(LABELLED_BY) === labelledBy) dialog.removeAttribute(LABELLED_BY);
        };
    }, [rootId, labelledBy, describedBy]);
    return null;
};
